import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '@/lib/firebase';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, Download, FileText, Lock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ImpactField {
  key: string;
  labelEn: string;
  labelFr: string;
  value: any;
  verified: boolean;
}

const LenderDealPreview: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();
  const [deal, setDeal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [ndaSigned, setNdaSigned] = useState(false);
  const [docs, setDocs] = useState<Array<{ name: string; category: string; url?: string; path: string }>>([]);

  const t = {
    en: {
      title: 'Deal Preview',
      back: 'Back',
      submit: 'Submit Financing Offer',
      overview: 'Deal Overview',
      impact: 'Impact Profile',
      documents: 'Documents',
      industry: 'Industry',
      location: 'Region',
      asking: 'Asking Price',
      ebitda: 'EBITDA',
      revenue: 'Gross Revenue',
      year: 'Year established',
      verified: 'Verified',
      selfDeclared: 'Self-declared',
      ndaRequired: 'Sign NDA to unlock documents',
      noDocs: 'No documents uploaded yet',
      employees: 'Number of employees',
      tenure: 'Average tenure (years)',
      retirement: 'Retirement plan',
      accidents: 'Workplace accidents (3y)',
      localSupply: 'Local supply chain %',
      cert: 'Certifications',
      litigation: 'Litigation (5y)',
      community: 'Community involvement',
      yes: 'Yes',
      no: 'No',
      notice: 'Self-declared by seller/broker. Not independently verified by AcquizGroup at this stage.',
    },
    fr: {
      title: 'Aperçu du Deal',
      back: 'Retour',
      submit: 'Soumettre une Offre de Financement',
      overview: 'Aperçu du Deal',
      impact: 'Profil d\'Impact',
      documents: 'Documents',
      industry: 'Industrie',
      location: 'Région',
      asking: 'Prix demandé',
      ebitda: 'EBITDA',
      revenue: 'Revenu brut',
      year: 'Année de création',
      verified: 'Vérifié',
      selfDeclared: 'Auto-déclaré',
      ndaRequired: 'Signez la NDA pour débloquer les documents',
      noDocs: 'Aucun document téléversé',
      employees: 'Nombre d\'employés',
      tenure: 'Ancienneté moyenne (années)',
      retirement: 'Régime de retraite',
      accidents: 'Accidents (3 ans)',
      localSupply: 'Chaîne locale %',
      cert: 'Certifications',
      litigation: 'Litiges (5 ans)',
      community: 'Implication communautaire',
      yes: 'Oui',
      no: 'Non',
      notice: 'Auto-déclaré par le vendeur/courtier. Non vérifié indépendamment par AcquizGroup à ce stade.',
    },
  }[language as 'en' | 'fr'] || ({} as any);

  useEffect(() => {
    if (!dealId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'deals', dealId));
        if (snap.exists()) setDeal({ id: snap.id, ...snap.data() });

        // NDA check
        const user = auth.currentUser;
        if (user) {
          const ndaQ = query(
            collection(db, 'ndaRequests'),
            where('dealId', '==', dealId),
            where('userId', '==', user.uid),
            where('status', '==', 'signed')
          );
          const ndaSnap = await getDocs(ndaQ);
          setNdaSigned(!ndaSnap.empty);
        }

        // Documents listed in Firestore (mirror of storage)
        const docsQ = query(collection(db, 'dealDocuments'), where('dealId', '==', dealId));
        const docsSnap = await getDocs(docsQ);
        const list: any[] = [];
        docsSnap.forEach((d) => {
          const data = d.data();
          list.push({ name: data.fileName || data.name, category: data.category || 'other', path: data.storagePath });
        });
        setDocs(list);
      } catch (e) {
        console.error('Failed to load deal preview', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [dealId]);

  const handleDownload = async (path: string, name: string) => {
    try {
      const url = await getDownloadURL(ref(storage, path));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.target = '_blank';
      a.click();
    } catch (e) {
      toast({ title: 'Download failed', variant: 'destructive' });
    }
  };

  const fmt = (n: number) => `$${Number(n || 0).toLocaleString('en-US')}`;

  const impactFields: ImpactField[] = deal ? [
    { key: 'employeeCount', labelEn: t.employees, labelFr: t.employees, value: deal.employeeCount ?? 0, verified: !!deal.impactDataVerified },
    { key: 'averageEmployeeTenureYears', labelEn: t.tenure, labelFr: t.tenure, value: deal.averageEmployeeTenureYears ?? 0, verified: !!deal.impactDataVerified },
    { key: 'hasRetirementPlan', labelEn: t.retirement, labelFr: t.retirement, value: deal.hasRetirementPlan ? t.yes : t.no, verified: !!deal.impactDataVerified },
    { key: 'workplaceAccidentsLast3yr', labelEn: t.accidents, labelFr: t.accidents, value: deal.workplaceAccidentsLast3yr ?? 0, verified: !!deal.impactDataVerified },
    { key: 'localSupplyChainPercent', labelEn: t.localSupply, labelFr: t.localSupply, value: `${deal.localSupplyChainPercent ?? 0}%`, verified: !!deal.impactDataVerified },
    { key: 'certifications', labelEn: t.cert, labelFr: t.cert, value: (deal.certifications || []).join(', ') || '-', verified: !!deal.impactDataVerified },
    { key: 'litigationLast5yr', labelEn: t.litigation, labelFr: t.litigation, value: deal.litigationLast5yr ? t.yes : t.no, verified: !!deal.impactDataVerified },
    { key: 'communityInvolvement', labelEn: t.community, labelFr: t.community, value: deal.communityInvolvement ? t.yes : t.no, verified: !!deal.impactDataVerified },
  ] : [];

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  if (!deal) return <div className="p-8 text-center text-muted-foreground">Deal not found</div>;

  return (
    <div className="container max-w-5xl py-8 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate(-1)}>{t.back}</Button>
        <h1 className="text-2xl font-bold">{t.title}</h1>
        <div className="w-20" />
      </div>

      {/* Section 1: Deal Overview (anonymized) */}
      <Card>
        <CardHeader><CardTitle>{t.overview}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t.industry} value={deal.industry || '-'} />
          <Field label={t.location} value={deal.region || deal.location || '-'} />
          <Field label={t.asking} value={fmt(deal.askingPrice)} />
          <Field label={t.ebitda} value={fmt(deal.ebitda)} />
          <Field label={t.revenue} value={fmt(deal.grossRevenue)} />
          <Field label={t.year} value={String(deal.yearEstablished || '-')} />
        </CardContent>
      </Card>

      {/* Section 2: Impact Profile */}
      <Card>
        <CardHeader>
          <CardTitle>{t.impact}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">{t.notice}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {impactFields.map((f) => (
            <div key={f.key} className="flex justify-between items-center border-b pb-2">
              <div>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? f.labelFr : f.labelEn}</p>
                <p className="font-medium">{String(f.value)}</p>
              </div>
              <Badge variant={f.verified ? 'default' : 'secondary'} className="gap-1">
                {f.verified ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {f.verified ? t.verified : t.selfDeclared}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Section 3: Documents */}
      <Card>
        <CardHeader><CardTitle>{t.documents}</CardTitle></CardHeader>
        <CardContent>
          {!ndaSigned ? (
            <div className="text-center py-6 text-muted-foreground flex flex-col items-center gap-2">
              <Lock className="h-8 w-8" />
              <p>{t.ndaRequired}</p>
            </div>
          ) : docs.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">{t.noDocs}</p>
          ) : (
            <div className="space-y-2">
              {docs.map((d, i) => (
                <div key={i} className="flex items-center justify-between border rounded p-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{d.category}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleDownload(d.path, d.name)}>
                    <Download className="h-4 w-4 mr-1" /> {language === 'fr' ? 'Télécharger' : 'Download'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button size="lg" onClick={() => navigate(`/forms/lender-offer?deal=${dealId}`)}>
          {t.submit}
        </Button>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);

export default LenderDealPreview;
